import React, { useRef, useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { useUser } from '@/lib/auth/useUser'
import { useTranslations } from 'next-intl'
import {
	Box,
	Center,
	Image,
	Flex,
	Icon,
	Text,
	useDisclosure,
	BoxProps,
	FlexProps,
	Grid,
} from '@chakra-ui/react'
import Navbar from './Navbar/Navbar'
import getIcon from '../lib/icons'
import dynamicIconImports from 'lucide-react/dynamicIconImports'
import NavbarLanding from './Landing/NavbarLanding'
import { TenantShell, hasTenantRoot } from './page/TenantPageRoot'
import LocaleSwitcher from './LocaleSwitcher'
import tenantData from '@crosure/tenant/data'
import tenantChrome from '@/lib/tenantChrome'
import {
	isSidebarRouteActive,
	useDashboardSidebar,
} from '../lib/dashboardSidebarContext'
import { clearTenantAdminSession } from '@/lib/tenantAdminAuth'
import { clearCustomerToken } from '@/lib/customerAuth'
import { getSponsorOrgName } from '@/lib/sponsorAuth'
const sidebar = (tenantData as any)?.translations?.sidebar ?? {
	admin: { items: [] },
	superAdmin: { items: [] },
	user: { items: [] },
}
const adminSidebar = sidebar.admin
const superAdminSidebar = sidebar.superAdmin
const userSidebar = sidebar.user

const DRAWER_WIDTH = 72

interface NavItemProps extends FlexProps {
	title: string
	slug: string
	icon?: keyof typeof dynamicIconImports
	items?: NavItemProps[]
}

interface SidebarProps extends BoxProps {
	isOpen: boolean
	data: any
	siteNavigation?: { logoUrl?: string }
}

interface Role {
	name: string
}

const Sidebar = ({ isOpen, siteNavigation }: SidebarProps) => {
	const t = useTranslations()
	const router = useRouter()
	const { asPath } = router
	// Studio's self-service logo upload (Settings → Site chrome → Logo,
	// site_navigation.logo_url) previously only reached the public-facing
	// NavbarLanding — this Sidebar read `t('logoWhite')` unconditionally,
	// a static value from the tenant package's own translations, with no
	// path for a Studio-configured logo to ever override it. Same
	// precedence as NavbarLanding: Studio's uploaded logo wins when set,
	// falling back to the tenant package's static logoWhite otherwise.
	const sidebarLogo = siteNavigation?.logoUrl || t('logoWhite')
	const { error, isLoading, user } = useUser() //useUser is React Hook from the Auth0 Next.js SDK that gives you access to the currently logged-in user’s information — on the client side (browser).
	const [sidebarData, setSidebarData] = useState<any[]>([])
	// Studio-driven customer/sponsor dashboards (pages/dashboard, pages/sponsor/
	// dashboard) push their own nav items here — one per published section —
	// via DashboardSidebarProvider (pages/_app.tsx). Takes priority over the
	// role-based list below, which only ever applies on non-dashboard-schema
	// pages (e.g. /cms) that still configure a static tenant sidebar.
	const { items: sectionItems, activeId, setActiveId } = useDashboardSidebar()
	// Mirrors the isSponsorPortal check in the outer Layout component (below)
	// — needed here too since Sidebar reads its own asPath rather than
	// receiving that flag as a prop.
	const isSponsorPortal =
		asPath.includes('/sponsor') &&
		!asPath.includes('/sponsor/login') &&
		!asPath.includes('/sponsor/forgot-password') &&
		!asPath.includes('/sponsor/reset-password') &&
		!asPath.includes('/sponsor/complete')
	const [sponsorOrgName, setSponsorOrgName] = useState<string | null>(null)
	useEffect(() => {
		if (isSponsorPortal) setSponsorOrgName(getSponsorOrgName())
	}, [isSponsorPortal])
	const isTenantAdminPortal =
		asPath.includes('/tenant-admin') &&
		!asPath.includes('/tenant-admin/login') &&
		!asPath.includes('/tenant-admin/forgot-password') &&
		!asPath.includes('/tenant-admin/reset-password')

	const handleTenantAdminLogout = React.useCallback(() => {
		clearTenantAdminSession()
		router.replace('/tenant-admin/login')
	}, [router])

	// startsWith, not includes: '/sponsor/dashboard' and
	// '/tenant-admin/dashboard' both contain '/dashboard' and would otherwise
	// get the customer sign-out (clearing the wrong token and bouncing the
	// user to the wrong login).
	const isCustomerPortal =
		asPath.startsWith('/dashboard') && !asPath.startsWith('/dashboard/login')

	const handleCustomerLogout = React.useCallback(() => {
		clearCustomerToken()
		router.replace('/dashboard/login')
	}, [router])

	useEffect(() => {
		const fetchUserRoles = async () => {
			if (!user) return
			try {
				//get user token id from useUser hook and use it to get user info
				const res = await fetch(`/api/user/${user.sub}`)
				if (!res.ok) throw new Error('Failed to fetch user roles')
				const data = await res.json()
				if (data.roles) {
					const isSuperAdmin = data.roles?.some(
						(role: Role) => role.name === 'Super Admin'
					)
					const isAdmin = data.roles?.some(
						(role: Role) => role.name === 'Admin'
					)

					let sidebarToUse = userSidebar
					if (isSuperAdmin) {
						sidebarToUse = superAdminSidebar
					} else if (isAdmin) {
						sidebarToUse = adminSidebar
					}

					setSidebarData(sidebarToUse.items)
				}
			} catch (err) {
				console.error('Error fetching roles:', err)
			}
		}

		if (!isLoading) {
			fetchUserRoles()
		}
	}, [user, isLoading])

	if (error) return <Text>{error.message}</Text>

	return (
		<Box
			transform={{
				base: isOpen ? 'translateX(0)' : 'translateX(-100%)',
				md: 'none',
			}}
			transition='0.2s'
			bgGradient='linear(to-br, gray.700, gray.900)'
			color='white'
			w={{ base: 'full', md: DRAWER_WIDTH }}
			display='flex'
			flexDir='column'
			flexShrink={0}
			position='fixed'
			left={0}
			top={0}
			bottom={0}
			zIndex={12}
			overflowY='auto'
		>
			<Flex
				alignItems='center'
				flexDir='column'
				mx='8'
				justifyContent='space-between'
			>
				<Center minH={{ base: 12, md: 20 }} flexShrink={0} p={3}>
					<Image src={sidebarLogo} alt='logo' h={{ base: '6', md: '9' }} />
				</Center>
			</Flex>

			{isSponsorPortal && sponsorOrgName && (
				<Flex align='center' gap={3} mx='6' mb='4'>
					<Center
						bg='whiteAlpha.200'
						color='white'
						borderRadius='full'
						w='9'
						h='9'
						fontSize='sm'
						fontWeight='bold'
						flexShrink={0}
					>
						{sponsorOrgName.charAt(0).toUpperCase()}
					</Center>
					<Box minW={0}>
						<Text fontSize='xs' color='gray.400'>
							Welcome,
						</Text>
						<Text fontSize='sm' fontWeight='semibold' noOfLines={1}>
							{sponsorOrgName}
						</Text>
					</Box>
				</Flex>
			)}

			{sectionItems.length > 0 ? (
				<Grid gap='1' mx='4'>
					{sectionItems.map(item =>
						item.href ? (
							<RouteNavItem
								key={item.id}
								href={item.href}
								title={item.label}
								icon={item.icon as keyof typeof dynamicIconImports}
								badgeCount={item.badgeCount}
							/>
						) : (
							<SectionNavItem
								key={item.id}
								id={item.id}
								title={item.label}
								icon={item.icon as keyof typeof dynamicIconImports}
								isActive={activeId === item.id}
								onNavigate={setActiveId}
							/>
						)
					)}
				</Grid>
			) : (
				!!sidebarData.length && (
					<Grid gap='1' mx='4'>
						{sidebarData.map(link => (
							<NavItem
								key={link.slug}
								title={link.title}
								slug={link.slug}
								icon={link.icon}
								items={link.items}
							/>
						))}
					</Grid>
				)
			)}

			{isTenantAdminPortal || isCustomerPortal ? (
				<Grid gap='1' mx='4' mt='4'>
					<ActionNavItem
						title='Sign out'
						icon='log-out'
						onClick={
							isTenantAdminPortal
								? handleTenantAdminLogout
								: handleCustomerLogout
						}
					/>
				</Grid>
			) : null}

			<Center mt='auto' py='4'>
				<Text color='gray.500' mr='2' fontSize='xs'>
					{t('dashboard.poweredBy')}
				</Text>
				<Image src={t('crosureLogo')} alt='Crosure' h='16px' />
			</Center>
		</Box>
	)
}

interface ActionNavItemProps {
	title: string
	icon: keyof typeof dynamicIconImports
	onClick: () => void
}

const ActionNavItem = ({ title, icon, onClick }: ActionNavItemProps) => (
	<Flex
		as='button'
		type='button'
		onClick={onClick}
		align='center'
		px='4'
		py='3'
		gap='4'
		borderRadius='lg'
		role='group'
		cursor='pointer'
		_hover={{
			color: 'white',
			bg: 'blackAlpha.500',
			textDecoration: 'none',
		}}
		color='gray.400'
		textAlign='left'
		w='full'
	>
		<Icon
			fontSize='2xl'
			strokeWidth={1.25}
			_groupHover={{ color: 'white' }}
			as={getIcon(icon)}
		/>
		{title}
	</Flex>
)

const NavItem = (props: NavItemProps) => {
	const { title, slug, icon, items = [], pl, fontSize } = props
	const { asPath } = useRouter()
	const isActive = asPath === slug
	const isChildActive = items.some(item => asPath === item.slug)
	return (
		<Box
			bg={isActive || isChildActive ? 'blackAlpha.500' : 'transparent'}
			borderRadius='lg'
		>
			<Flex
				as={Link}
				href={slug}
				style={{ textDecoration: 'none' }}
				_focus={{ boxShadow: 'none' }}
				align='center'
				px='4'
				py='3'
				pl={pl}
				gap='4'
				fontSize={fontSize}
				borderRadius='lg'
				role='group'
				cursor='pointer'
				_hover={{
					color: 'white',
					bg: 'blackAlpha.500',
					textDecoration: 'none',
				}}
				color='gray.400'
				bg={isActive ? 'black' : 'transparent'}
				textColor={isActive ? 'white' : 'gray.300'}
			>
				{icon && (
					<Icon
						fontSize='2xl'
						strokeWidth={1.25}
						_groupHover={{
							color: 'white',
						}}
						as={getIcon(icon)}
					/>
				)}
				{title}
			</Flex>
			{(isActive || isChildActive) &&
				items.map(subItem => (
					<NavItem key={subItem.slug} {...subItem} pl='14' fontSize='sm' />
				))}
		</Box>
	)
}

interface RouteNavItemProps {
	href: string
	title: string
	icon: keyof typeof dynamicIconImports
	/** Small unread/needs-attention count (e.g. sponsor portal's Orders item
	 * — pending orders) — ooredoo-ui's employer sidebar shows the same kind
	 * of badge on its Orders link. Omitted or 0 renders nothing. */
	badgeCount?: number
}

/** One sidebar entry for a real routed page (the sponsor multi-product
 * portal — pages/sponsor/{dashboard,new-policy,orders,...}.tsx) — a Link,
 * active on its route and drill-ins (isSidebarRouteActive), same visual treatment as SectionNavItem/NavItem. */
const RouteNavItem = ({ href, title, icon, badgeCount }: RouteNavItemProps) => {
	const { asPath } = useRouter()
	const isActive = isSidebarRouteActive(asPath, href)
	return (
		<Flex
			as={Link}
			href={href}
			style={{ textDecoration: 'none' }}
			_focus={{ boxShadow: 'none' }}
			align='center'
			px='4'
			py='3'
			gap='4'
			borderRadius='lg'
			role='group'
			cursor='pointer'
			_hover={{ color: 'white', bg: 'blackAlpha.500' }}
			color='gray.400'
			bg={isActive ? 'black' : 'transparent'}
			textColor={isActive ? 'white' : 'gray.300'}
		>
			<Icon
				fontSize='2xl'
				strokeWidth={1.25}
				_groupHover={{ color: 'white' }}
				as={getIcon(icon)}
			/>
			<Text flex='1'>{title}</Text>
			{!!badgeCount && (
				<Center
					bg='red.500'
					color='white'
					borderRadius='full'
					minW='5'
					h='5'
					px='1'
					fontSize='xs'
					fontWeight='bold'
				>
					{badgeCount}
				</Center>
			)}
		</Flex>
	)
}

interface SectionNavItemProps {
	id: string
	title: string
	icon: keyof typeof dynamicIconImports
	isActive: boolean
	onNavigate: (id: string) => void
}

/** One sidebar entry per published dashboard section — same-page anchor
 * scroll, not a route change (the dashboard is a single page; sections are
 * identified by `id={section_type}` in pages/dashboard/index.tsx). Active
 * state normally comes from that page's scroll-spy via
 * DashboardSidebarProvider, but a click sets it immediately too — on a short
 * dashboard where every section already fits on screen, scrollIntoView is a
 * no-op and the IntersectionObserver never re-fires, so without this a click
 * would otherwise appear to do nothing. */
const SectionNavItem = ({
	id,
	title,
	icon,
	isActive,
	onNavigate,
}: SectionNavItemProps) => {
	const handleClick = (e: React.MouseEvent) => {
		e.preventDefault()
		onNavigate(id)
		document
			.getElementById(id)
			?.scrollIntoView({ behavior: 'smooth', block: 'start' })
	}
	return (
		<Flex
			as='a'
			href={`#${id}`}
			onClick={handleClick}
			style={{ textDecoration: 'none' }}
			_focus={{ boxShadow: 'none' }}
			align='center'
			px='4'
			py='3'
			gap='4'
			borderRadius='lg'
			role='group'
			cursor='pointer'
			_hover={{ color: 'white', bg: 'blackAlpha.500' }}
			color='gray.400'
			bg={isActive ? 'black' : 'transparent'}
			textColor={isActive ? 'white' : 'gray.300'}
		>
			<Icon
				fontSize='2xl'
				strokeWidth={1.25}
				_groupHover={{ color: 'white' }}
				as={getIcon(icon)}
			/>
			{title}
		</Flex>
	)
}

const Layout = (props: any) => {
	const { isOpen, onOpen, onClose } = useDisclosure()
	const { asPath, events } = useRouter()
	const { children, data } = props
	const scrollRef = useRef<null | HTMLDivElement>(null)
	// The customer login page is pre-auth — no dashboard sidebar until signed in.
	// The sponsor multi-product portal (/sponsor/dashboard, /sponsor/employees,
	// /sponsor/new-policy, etc.) needs the same authenticated Sidebar chrome —
	// this used to only match by accident on /sponsor/dashboard itself (its
	// path happens to contain the substring "/dashboard"), so every other
	// sponsor portal page silently fell through to isLanding's public
	// NavbarLanding instead, with no sidebar at all. /sponsor/login,
	// /sponsor/forgot-password, /sponsor/reset-password/[token] (all pre-auth)
	// and /sponsor/complete/[token] (the employee's own invite-token
	// completion wizard, not the sponsor's admin portal) are excluded for the
	// same reason /dashboard/login is.
	const isSponsorPortal =
		asPath.includes('/sponsor') &&
		!asPath.includes('/sponsor/login') &&
		!asPath.includes('/sponsor/forgot-password') &&
		!asPath.includes('/sponsor/reset-password') &&
		!asPath.includes('/sponsor/complete')
	// Same accidental-substring-match trap as isSponsorPortal above:
	// /tenant-admin/dashboard matched isDashboard by luck (contains
	// "/dashboard"), but /tenant-admin/claims and every other tenant-admin
	// portal page added later would silently fall through to the public
	// NavbarLanding chrome instead. /tenant-admin/login,
	// /tenant-admin/forgot-password and /tenant-admin/reset-password/[token]
	// are pre-auth, same treatment as /dashboard/login and /sponsor/login.
	const isTenantAdminPortal =
		asPath.includes('/tenant-admin') &&
		!asPath.includes('/tenant-admin/login') &&
		!asPath.includes('/tenant-admin/forgot-password') &&
		!asPath.includes('/tenant-admin/reset-password')
	const isDashboard =
		(asPath.includes('/dashboard') && !asPath.includes('/dashboard/login')) ||
		asPath.includes('/cms') ||
		isSponsorPortal ||
		isTenantAdminPortal
	const isForm =
		asPath.includes('form') ||
		asPath.includes('/paymentConfirm') ||
		asPath.includes('/fail')
	const isLanding = !isDashboard && !isForm
	// The tenant's own header on the form journey, when Studio opted the
	// tenant in (page_builder.root_on_forms) and the page loaded it — see
	// lib/brandingProps.ts loadFormBrandingProps. It replaces the skeleton
	// Navbar; anything short of that keeps today's chrome.
	const shellRoot: Record<string, unknown> | null =
		isForm && data?.shellRoot && hasTenantRoot() ? data.shellRoot : null

	// close drawer on route change
	React.useEffect(() => {
		events.on('routeChangeComplete', onClose)
		return () => events.off('routeChangeComplete', onClose)
	}, [onClose, events])

	// Scroll to top on route change
	React.useEffect(() => {
		if (!scrollRef.current) return
		scrollRef.current.scrollTo(0, 0)
	}, [asPath])

	const scroller = (
		<Flex
			overflowY='auto'
			flexDir='column'
			flex={1}
			ref={scrollRef}
			data-scrollable
		>
			{children}
		</Flex>
	)

	return (
		<Flex bg={isLanding ? 'white' : 'gray.50'} position='fixed' inset='0'>
			{isDashboard && (
				<Sidebar
					isOpen={isOpen}
					data={data?.translations}
					siteNavigation={data?.siteNavigation}
				/>
			)}
			<Flex
				flexDir='column'
				flex={1}
				minW='0'
				ml={{ md: isDashboard ? DRAWER_WIDTH : 0 }}
			>
				{isLanding ? (
					// A tenant whose own pages ship a header opts out of this one —
					// it is position:fixed, so it would overlay theirs rather than
					// replace it (tenant config.chrome.landingNavbar).
					//
					// `ownsChrome` is the per-PAGE form of the same opt-out: a BYOD
					// page renders the tenant's page-builder root, which brings its
					// own header and footer. Per page rather than per tenant,
					// because the same tenant's coded pages still want this navbar.
					tenantChrome.landingNavbar &&
					!data?.ownsChrome && (
						<NavbarLanding
							data={data?.translations}
							siteNavigation={data?.siteNavigation}
							scrollRef={scrollRef}
						/>
					)
				) : shellRoot ? null : (
					<Navbar
						data={data?.translations}
						siteNavigation={data?.siteNavigation}
						onToggle={isOpen ? onClose : onOpen}
					/>
				)}
				{shellRoot ? (
					// Around the scroller, not the page: the header takes the
					// navbar's place and the form inside scrolls exactly as before.
					<TenantShell
						rootProps={shellRoot}
						homeHref={data?.siteNavigation?.homeHref}
						localeSwitcher={<LocaleSwitcher />}
					>
						{scroller}
					</TenantShell>
				) : (
					scroller
				)}
			</Flex>
		</Flex>
	)
}

export default Layout
